import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./TopMindsBadge.css";

// Gives every badge instance its own gradient ids so several badges
// on one page never clash.
let badgeCount = 0;

const Medallion = ({ uid, large }) => (
  <svg
    className={large ? "tm-medal tm-medal-lg" : "tm-medal"}
    viewBox="0 0 240 224"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#F8E3A3" />
        <stop offset="0.5" stopColor="#D9A441" />
        <stop offset="1" stopColor="#9C6A1B" />
      </linearGradient>
      <radialGradient id={`${uid}-green`} cx="0.5" cy="0.4" r="0.7">
        <stop offset="0" stopColor="#1F5C46" />
        <stop offset="1" stopColor="#0A261C" />
      </radialGradient>
      <linearGradient id={`${uid}-spark`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFF1C2" />
        <stop offset="1" stopColor="#E5AE45" />
      </linearGradient>
    </defs>
    <circle cx="120" cy="112" r={large ? 104 : 106} fill={`url(#${uid}-gold)`} />
    {large && (
      <circle
        cx="120"
        cy="112"
        r="97"
        fill="none"
        stroke="#FFF1C2"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="0 11.08"
        opacity="0.9"
      />
    )}
    <circle cx="120" cy="112" r="88" fill={`url(#${uid}-green)`} />
    <circle
      cx="120"
      cy="112"
      r="78"
      fill="none"
      stroke={`url(#${uid}-gold)`}
      strokeWidth={large ? 2 : 4}
    />
    <g transform="translate(0,14)" fill={`url(#${uid}-spark)`}>
      <path d="M120 48C124 82 136 94 170 98C136 102 124 114 120 148C116 114 104 102 70 98C104 94 116 82 120 48Z" />
      <path d="M84 60C85 67 88 69 95 70C88 71 85 73 84 80C83 73 80 71 73 70C80 69 83 67 84 60Z" />
      <path d="M156 114C157 119 159 121 164 122C159 123 157 125 156 130C155 125 153 123 148 122C153 121 155 119 156 114Z" />
    </g>
  </svg>
);

const TopMindsBadge = ({ className = "" }) => {
  const idRef = useRef(null);
  if (idRef.current === null) {
    badgeCount += 1;
    idRef.current = `tm${badgeCount}`;
  }
  const baseId = idRef.current;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // stopPropagation so tapping the badge on an idea card
  // doesn't also open the idea.
  const openPopup = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  };

  const onOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) setOpen(false);
  };

  const closePopup = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`tm-tag ${className}`.trim()}
        onClick={openPopup}
        aria-label="Top Minds award. Tap to learn how to earn it"
      >
        <Medallion uid={`${baseId}a`} />
        <span className="tm-tag-label">Top Minds</span>
      </button>

      {open &&
        createPortal(
          <div className="tm-overlay" onClick={onOverlayClick}>
            <div
              className="tm-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={`${baseId}-title`}
            >
              <Medallion uid={`${baseId}b`} large />
              <h2 id={`${baseId}-title`} className="tm-sheet-title">
                Top Minds
              </h2>
              <p className="tm-sheet-text">
                Top Minds is awarded when your idea is selected for Top Sparks
                through competitions.
              </p>
              <button type="button" className="tm-sheet-btn" onClick={closePopup}>
                Got it
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default TopMindsBadge;